import type {
  ReportSectionDto,
  ReportStatus,
  SectionMetaDto,
} from '@act/shared/data-transfer-objects';
import { SECTION_LIBRARY } from '@act/shared/data-transfer-objects';

/** Human-readable labels for report lifecycle statuses. */
export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  active: 'Active',
  archived: 'Archived',
};

/**
 * Formats an ISO timestamp for display in lists and headers.
 * @param iso - ISO-8601 date string.
 * @returns Locale-formatted date/time string.
 */
export const formatReportTimestamp = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

/**
 * Resolves section library metadata by section id.
 * @param sectionId - Library section identifier.
 * @returns Matching metadata or undefined.
 */
export const getSectionMeta = (
  sectionId: string,
): SectionMetaDto | undefined => {
  return SECTION_LIBRARY.find(section => section.id === sectionId);
};

/** Live report section row used for preview and print. */
export type PreviewSection = ReportSectionDto & { source: 'draft' };

/**
 * Builds preview/print sections from live report section rows.
 * @param sections - Live report section rows.
 * @returns Included sections sorted by order.
 */
export const buildReportPreviewSections = (
  sections: ReportSectionDto[],
): PreviewSection[] => {
  return sections
    .filter(section => section.included)
    .sort((a, b) => a.order - b.order)
    .map(section => ({ ...section, source: 'draft' as const }));
};

/**
 * Converts unknown snapshot values into readable display strings.
 * @param value - Snapshot field value.
 * @returns String suitable for Typography display.
 */
export const snapshotValueToDisplay = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return JSON.stringify(value, null, 2);
};
